import { useState } from "react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { Button } from "#/components/common/Button";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useMarkNotificationRead, useNotifications } from "../hooks";

function formatDate(value?: string | null) {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
}

export default function NotificationInboxPage() {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const notificationsQuery = useNotifications(page, size);
  const markReadMutation = useMarkNotificationRead();

  if (notificationsQuery.isLoading) {
    return <PageLoader label="Loading notifications..." />;
  }

  if (notificationsQuery.isError) {
    return (
      <ErrorState
        title="Failed to load notifications"
        message={
          notificationsQuery.error instanceof Error
            ? notificationsQuery.error.message
            : "Could not load notifications."
        }
      />
    );
  }

  const data = notificationsQuery.data;
  const items = data?.content ?? [];

  if (!data || items.length === 0) {
    return (
      <EmptyState
        title="No notifications"
        description="Your recent notifications and alerts will appear here."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Notifications</h1>
        <p className="text-sm text-slate-500">
          View recent alerts, workflow updates, and system messages.
        </p>
      </div>

      <div className="space-y-3">
        {items.map((item) => {
          const unread = !item.readAt;

          return (
            <div
              key={item.id}
              className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-slate-900">
                      {item.title}
                    </h2>

                    {unread ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                        New
                      </span>
                    ) : null}
                  </div>

                  {item.message ? (
                    <p className="mt-2 text-sm text-slate-600">{item.message}</p>
                  ) : null}

                  <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
                    {item.status ? <span>Status: {item.status}</span> : null}
                    <span>Created: {formatDate(item.createdAt)}</span>
                    <span>Read: {item.readAt ? formatDate(item.readAt) : "No"}</span>
                  </div>
                </div>

                {unread ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={markReadMutation.isPending}
                    onClick={() => markReadMutation.mutate(item.id)}
                  >
                    Mark as read
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <DataPagination
        page={data.number}
        totalPages={data.totalPages}
        totalElements={data.totalElements}
        size={data.size}
        onPageChange={setPage}
        onSizeChange={(nextSize) => {
          setSize(nextSize);
          setPage(0);
        }}
      />
    </div>
  );
}